import { useEffect, useMemo, useState } from 'react';
import {
  functionalUpdate,
  type ColumnOrderState,
  type ColumnVisibilityState,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type Updater,
} from '@tanstack/react-table';
import { getCoreRowModel, getSortedRowModel, useLegacyTable } from '@tanstack/react-table/legacy';
import { useLocalStorage } from '@/hooks/use-local-storage';
import type { ListSort } from '@/hooks/list-params-serde';
import type { DataTableColumn, DataTableDensity } from './types';
import type { ColumnEntry } from './data-table-columns-menu';

interface TablePreferences {
  visibility: ColumnVisibilityState;
  order: ColumnOrderState;
  density: DataTableDensity;
}

const EMPTY_PREFERENCES: TablePreferences = { visibility: {}, order: [], density: 'default' };

/** Stored order first, then anything it has not heard of yet (a new column). */
function mergeOrder(natural: string[], stored: ColumnOrderState): string[] {
  const known = new Set(natural);
  const ordered = stored.filter((id) => known.has(id));
  return [...ordered, ...natural.filter((id) => !ordered.includes(id))];
}

export interface UseDataTableOptions<TData extends RowData> {
  tableId: string;
  data: readonly TData[];
  columns: readonly DataTableColumn<TData>[];
  getRowId: (row: TData) => string;
  selectable: boolean;
  sort?: ListSort | null;
  onSortChange?: (sort: ListSort | null) => void;
  manualSorting?: boolean;
  density?: DataTableDensity;
  onDensityChange?: (density: DataTableDensity) => void;
  columnLabels?: Record<string, string>;
  defaultHiddenColumns?: readonly string[];
}

const EMPTY_HIDDEN: readonly string[] = [];

/**
 * The view's seed, overridden by whatever the person chose in the column menu.
 *
 * Written as a seed rather than as a stored preference so that "Restablecer
 * columnas" goes back to the view's opinion instead of showing everything.
 */
function seedVisibility(
  stored: ColumnVisibilityState,
  hidden: readonly string[],
): ColumnVisibilityState {
  if (hidden.length === 0) return stored;
  const merged: ColumnVisibilityState = {};
  for (const id of hidden) merged[id] = false;
  return { ...merged, ...stored };
}

/**
 * Table instance plus the preferences that outlive the session.
 *
 * Column layout and density are remembered per `tableId` in localStorage;
 * sorting is deliberately *not*, because it belongs in the URL where it can be
 * shared (see `useListParams`).
 */
export function useDataTable<TData extends RowData>({
  tableId,
  data,
  columns,
  getRowId,
  selectable,
  sort,
  onSortChange,
  manualSorting = false,
  density,
  onDensityChange,
  columnLabels,
  defaultHiddenColumns = EMPTY_HIDDEN,
}: UseDataTableOptions<TData>) {
  const [stored, setPreferences] = useLocalStorage<TablePreferences>(
    `crm.table.${tableId}`,
    EMPTY_PREFERENCES,
  );
  // Preferences written by an older build of this table are still in the
  // browser; a missing key must not take the whole view down.
  const preferences: TablePreferences = {
    visibility: seedVisibility(stored?.visibility ?? {}, defaultHiddenColumns),
    order: Array.isArray(stored?.order) ? stored.order : [],
    density: stored?.density === 'compact' ? 'compact' : 'default',
  };
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);

  /**
   * Selection cannot outlive the rows it points at.
   *
   * Keeping it whole across a page change made the bulk bar vanish on page 2
   * and reappear on the way back — two states that look identical and mean
   * different things — while a bulk action could only reach the rows still in
   * memory. Rows that are gone drop out of the selection; a refetch that brings
   * the same ids back keeps it.
   */
  const present = useMemo(() => new Set(data.map((row) => getRowId(row))), [data, getRowId]);
  const stale = Object.keys(rowSelection).filter((id) => !present.has(id));
  useEffect(() => {
    if (stale.length === 0) return;
    setRowSelection((current) => {
      const next: RowSelectionState = {};
      for (const [id, selected] of Object.entries(current)) {
        if (present.has(id)) next[id] = selected;
      }
      return next;
    });
    // Keyed on the ids themselves: `present` is a new Set on every render.
  }, [stale.join(','), present]);

  const controlled = Boolean(onSortChange);
  const sorting = useMemo<SortingState>(
    () => (controlled ? (sort ? [{ id: sort.id, desc: sort.dir === 'desc' }] : []) : internalSorting),
    [controlled, sort, internalSorting],
  );

  const table = useLegacyTable<TData>({
    data,
    columns,
    getRowId,
    enableRowSelection: selectable,
    manualSorting,
    state: {
      sorting,
      rowSelection,
      columnVisibility: preferences.visibility,
      columnOrder: preferences.order,
    },
    onSortingChange: (updater: Updater<SortingState>) => {
      const next = functionalUpdate(updater, sorting);
      if (!onSortChange) {
        setInternalSorting(next);
        return;
      }
      const first = next[0];
      onSortChange(first ? { id: first.id, dir: first.desc ? 'desc' : 'asc' } : null);
    },
    onRowSelectionChange: (updater: Updater<RowSelectionState>) =>
      setRowSelection((prev) => functionalUpdate(updater, prev)),
    onColumnVisibilityChange: (updater: Updater<ColumnVisibilityState>) =>
      setPreferences((prev) => ({ ...prev, visibility: functionalUpdate(updater, prev.visibility) })),
    onColumnOrderChange: (updater: Updater<ColumnOrderState>) =>
      setPreferences((prev) => ({ ...prev, order: functionalUpdate(updater, prev.order) })),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const leafColumns = table.getAllLeafColumns();
  const order = mergeOrder(
    leafColumns.map((column) => column.id),
    preferences.order,
  );

  const columnEntries: ColumnEntry[] = order.flatMap((id) => {
    const column = leafColumns.find((candidate) => candidate.id === id);
    if (!column) return [];
    const header = column.columnDef.header;
    return [
      {
        id,
        label: columnLabels?.[id] ?? (typeof header === 'string' ? header : id),
        visible: column.getIsVisible(),
        canHide: column.getCanHide(),
      },
    ];
  });

  return {
    table,
    rowSelection,
    setRowSelection,
    density: density ?? preferences.density,
    setDensity: (next: DataTableDensity) => {
      setPreferences((prev) => ({ ...prev, density: next }));
      onDensityChange?.(next);
    },
    columnEntries,
    toggleColumn: (id: string, visible: boolean) =>
      setPreferences((prev) => ({ ...prev, visibility: { ...prev.visibility, [id]: visible } })),
    moveColumn: (id: string, direction: -1 | 1) => {
      const from = order.indexOf(id);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= order.length) return;
      const next = [...order];
      const [moved] = next.splice(from, 1);
      if (moved !== undefined) next.splice(to, 0, moved);
      setPreferences((prev) => ({ ...prev, order: next }));
    },
    resetColumns: () => setPreferences((prev) => ({ ...prev, visibility: {}, order: [] })),
  };
}
